import axios from "axios";

/**
 * Cliente HTTP central do GDoc.
 *
 * Antes desta migração, cada componente recriava
 * `const BASE_URL = ${REACT_APP_URL_BASE}:${REACT_APP_PORT_BACKEND}` e usava o
 * `axios` global diretamente; os interceptors (auth, refresh-token, 401 e
 * mensalidade em atraso) ficavam em `App.js`. Agora existe uma única instância
 * `api` (default export) que carrega esses interceptors, e um único `BASE_URL`
 * (named export) reaproveitado por quem ainda precisa da URL absoluta
 * (downloads via <a href>, redirecionamento do Google OAuth, fetch e pdf.js).
 *
 * Chamadas podem usar caminho relativo (`api.get("/api/x")`, resolvido pelo
 * `baseURL`) ou a URL absoluta legada (`api.get(`${BASE_URL}/api/x`)`); o axios
 * ignora o `baseURL` quando a URL já é absoluta, então ambos funcionam.
 */
export const BASE_URL = `${process.env.REACT_APP_URL_BASE}:${process.env.REACT_APP_PORT_BACKEND}`;

const api = axios.create({ baseURL: BASE_URL });

// Interceptor de request: envia o token em todas as requisições (evita 401 em cadastros/modais)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Item #26: renovação silenciosa via refresh token (cookie httpOnly) antes de
// forçar logout por 401 — evita derrubar a sessão só porque o JWT (2h) expirou.
let renovacaoEmAndamento = null;

function tentarRenovarToken() {
  if (!renovacaoEmAndamento) {
    renovacaoEmAndamento = api
      .post(`/api/auth/refresh-token`, {}, { withCredentials: true })
      .then(({ data }) => (data?.token ? data.token : null))
      .catch(() => null)
      .finally(() => {
        renovacaoEmAndamento = null;
      });
  }
  return renovacaoEmAndamento;
}

const ROTAS_AUTH_SEM_RENOVACAO = /\/api\/auth\/(login|register|refresh-token)\b/;

// Interceptor: ao receber 401, tenta renovar silenciosamente via refresh token antes de
// limpar o token e redirecionar para login (evita loop se já estiver em /login).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const codigo = error.response?.data?.codigo;
    const originalRequest = error.config || {};
    const url = originalRequest.url || "";

    if (
      status === 401 &&
      !ROTAS_AUTH_SEM_RENOVACAO.test(url) &&
      !originalRequest._tentouRenovar
    ) {
      originalRequest._tentouRenovar = true;
      const novoToken = await tentarRenovarToken();
      if (novoToken) {
        localStorage.setItem("authToken", novoToken);
        return api(originalRequest);
      }
    }

    if (status === 401) {
      const method = (error.config?.method || "GET").toUpperCase();
      // Log para debug: qual requisição causou o 401 (ative "Preserve log" no Console do DevTools)
      console.warn("[401] Redirecionando para login. Requisição que falhou:", method, url || "?");
      localStorage.removeItem("authToken");
      localStorage.removeItem("permissoes_grupo");
      const path = window.location.pathname || "";
      if (!path.toLowerCase().startsWith("/login")) {
        window.location.href = "/login";
      }
    }

    if (status === 403 && codigo === "MENSALIDADE_EM_ATRASO") {
      // O interceptor roda fora da árvore React (module scope), sem acesso a
      // setGlobalData — dispara um evento global que AppInner escuta, cobrindo
      // o caso do bloqueio ser detectado no meio de uma sessão já aberta.
      window.dispatchEvent(
        new CustomEvent("mensalidade:bloqueado", { detail: error.response.data })
      );
    }

    return Promise.reject(error);
  }
);

export default api;
